// LICENSE_CODE ZON
'use strict'; /*jslint react:true*/
define(['react', 'react-dom', 'react-router-dom', 'lodash4', 'react-i18next',
    '/util/setdb.js', '/util/etask.js', '/util/url.js', '/util/string.js',
    '/util/conv.js', '/www/util/pub/util.js', '/www/loading/pub/loading.js',
    '/util/date.js'],
    (React, ReactDOM, {Prompt, generatePath}, _, {useTranslation, Trans},
    setdb, etask, zurl, string, conv, pub_util, zloading, date)=>
{
const E = {};
const {qw} = string;
const {assign} = Object;

E.use_state = React.useState;
E.use_memo = React.useMemo;
E.use_cb = React.useCallback;
E.use_callback = React.useCallback;
E.use_layout_effect = React.useLayoutEffect;
E.use_imperative_handle = React.useImperativeHandle;
E.use_ref = React.useRef;
E.forward_ref = React.forwardRef;
E.use_reducer = React.useReducer;
E.use_context = React.useContext;

E.use_effect = (cb, deps)=>React.useEffect(()=>{
    let ret = cb();
    if (!ret)
        return;
    if (ret.constructor.name=='Etask')
        return ()=>ret.return();
    return ret;
}, deps);

E.use_setdb_on = (path, handler, opt, deps)=>{
    const cb = E.use_cb(handler, [path].concat(deps||[]));
    E.use_effect(()=>{
        const listener = setdb.on(path, cb, opt);
        return ()=>setdb.off(listener);
    }, [path, cb]);
};
E.use_setdb = (path, recursive)=>{
    const [value, set_value] = E.use_state(setdb.get(path));
    E.use_effect(()=>{
        const listeners = [setdb.on(path, set_value, {init: false})];
        if (recursive)
        {
            const notify = ()=>{
                const v = setdb.get(path);
                set_value(_.isObject(v) ? {...v} : v);
            };
            let _path = path;
            while (_path = _path.substr(0, _path.lastIndexOf('.')))
                listeners.push(setdb.on(_path, notify, {init: false}));
        }
        set_value(setdb.get(path)); // update one more time to fix race cond-s
        return ()=>listeners.forEach(setdb.off);
    }, [path, recursive]);
    return value;
};

E.use_etask = ()=>{
    const parent = E.use_memo(()=>etask('hook',
        function*(){ yield this.wait(); }), []);
    const etask_hook = E.use_cb(
        sp=>parent.spawn(sp.constructor.name!='Etask' ? etask(sp) : sp), []);
    E.use_effect(()=>()=>parent.return(), []);
    return etask_hook;
};

E.use_flag = initial=>{
    const [value, set_value] = E.use_state(initial);
    const toggle_value = E.use_cb(()=>set_value(v=>!v), []);
    return [value, toggle_value, set_value];
};

E.use_hover = el=>{
    let [hover, set_hover] = E.use_state(false);
    E.use_event_listener(el, 'mouseover', ()=>set_hover(true));
    E.use_event_listener(el, 'mouseleave', ()=>set_hover(false));
    return hover;
};

E.use_object_memo = obj=>E.use_memo(()=>obj, Object.values(obj));

E.use_classic_state = initial_state=>{
    const [state, set_state] = E.use_state(initial_state||{});
    const _set_state = E.use_cb(change=>set_state(_state=>({..._state,
        ..._.isFunction(change) ? change(_state) : change})), []);
    return [state, _set_state];
};

E.use_input_change = (handler, deps)=>
    E.use_cb(e=>handler(e.target.value), deps||[handler]);

E.use_enter_keypress = (handler, deps)=>E.use_cb(e=>{
    if (e.key!='Enter')
        return;
    handler();
}, deps||[handler]);

E.use_debounced_state = (initial, debounce)=>{
    const [value, set_value] = E.use_state(initial);
    const [debounced, set_debounced] = E.use_state(initial);
    const on_change = E.use_memo(()=>{
        const fn_debounced = _.debounce(v=>set_debounced(v), debounce);
        return v=>{
            set_value(v);
            fn_debounced(v);
        };
    }, [debounce]);
    return [value, debounced, on_change];
};

E.use_debounced_cb = (cb, wait=50)=>{
    const handler = E.use_cb(
        _.debounce(cb, wait, {leading: false, trailing: true}),
        [cb, wait],
    );
    return handler;
};

E.use_teardown = (cached_fn, wait=100)=>{
    const teardown = E.use_debounced_cb(cached_fn, wait);
    const fn_ref = E.use_ref();
    E.use_effect(()=>{ fn_ref.current = teardown; }, [teardown]);
    E.use_effect(()=>{
        fn_ref.current.cancel();
        return ()=>fn_ref.current();
    }, []);
};

E.use_update_only_effect = (effect, deps)=>{
    const mounted = E.use_ref(false);
    E.use_effect(()=>{
        mounted.current = true;
        return ()=>{ mounted.current = false; };
    }, []);
    const initial = E.use_ref(true);
    E.use_effect(()=>{
        let cleanup = _.noop;
        if (initial.current)
            initial.current = false;
        else
            cleanup = effect() || cleanup;
        return ()=>{
            cleanup();
            if (!mounted.current)
                initial.current = true;
        };
    }, deps);
};

E.use_ref_dimensions = ()=>{
    const ref = E.use_ref();
    const [dim, set_dim] = E.use_state({});
    const invalidate = E.use_cb(()=>set_dim(ref.current
        ? ref.current.getBoundingClientRect().toJSON()
        : {}), []);
    E.use_layout_effect(invalidate, [ref.current]);
    return [ref, dim, invalidate];
};

E.use_event_listener = (target, event, handler, deps=[])=>E.use_effect(()=>{
    const [evt, opt] = [].concat(event);
    if (!target || !evt || !handler)
        return;
    target.addEventListener(evt, handler, opt);
    return ()=>target.removeEventListener(evt, handler, opt);
}, [target, ...deps]);

E.use_window_size = ()=>{
    const make_size_obj = ()=>({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [size, set_size] = E.use_state(make_size_obj());
    const deb_delay = 200;
    const on_resize = E.use_cb(
        _.debounce(()=>set_size(make_size_obj()), deb_delay),
        [window]);
    E.use_event_listener(window, 'resize', on_resize, [on_resize]);
    return size;
};

E.use_debounce = (value, delay)=>{
    const [debounced_value, set_debounced_value] = E.use_state(value);
    E.use_effect(()=>{
        const timer = setTimeout(()=>set_debounced_value(value), delay);
        return ()=>clearTimeout(timer);
    }, [value, delay]);
    return debounced_value;
};

E.use_interval = (cb, delay, mode)=>{
    const saved_callback = E.use_ref();
    E.use_effect(()=>{ saved_callback.current = cb; }, [cb]);
    E.use_effect(()=>{
        const tick = ()=>saved_callback.current();
        if (delay!=null)
        {
            let et = etask.interval({ms: delay, mode}, tick);
            return ()=>et.return();
        }
      }, [delay, mode]);
};

E.use_key_press = key=>{
    const [key_pressed, set_key_pressed] = E.use_state(false);
    const [check_key, is_special] = E.use_memo(()=>{
        if (qw`shift alt ctrl`.includes(key))
            return [evt=>!!evt[`${key}Key`], true];
        return [evt=>evt.key===key, false];
    }, [key]);
    const down_handler = evt=>{
        if (!check_key(evt))
            return;
        set_key_pressed(true);
    };
    const up_handler = evt=>{
        if (is_special ? check_key(evt) : !check_key(evt))
            return;
        set_key_pressed(false);
    };
    E.use_effect(()=>{
        window.addEventListener('keydown', down_handler);
        window.addEventListener('keyup', up_handler);
        return ()=>{
            window.removeEventListener('keydown', down_handler);
            window.removeEventListener('keyup', up_handler);
        };
    }, []);
    return key_pressed;
};

E.use_request = ()=>{
    const [processing, set_pross] = E.use_state(false);
    const [error, set_error] = E.use_state(null);
    const etask_hook = E.use_etask();
    const wrap = E.use_cb(req=>etask_hook(function*(){
        this.on('uncaught', e=>{
            console.error(e);
            set_error(e);
        });
        this.on('finally', ()=>set_pross(false));
        set_pross(true);
        const res = yield req;
        if (res && res.error)
            set_error(res.error);
        else
            set_error(null);
        return res;
    }), []);
    const meta = E.use_memo(()=>({processing, error}),
        [processing, error]);
    return [wrap, etask_hook, meta];
};

E.use_form = (fields={}, opt={})=>{
    const validate = E.use_memo(()=>(id, value)=>{
        const field = _.get(fields, id);
        if (!field)
            return;
        if (field.required &&
            (!value || Array.isArray(value) && _.isEmpty(value)))
        {
            return field.required_msg || opt.required_msg ||
                'This value is required';
        }
        if (field.type=='file')
        {
            let validate_file = file=>{
                let file_errors = pub_util.validate_file(file, {require: false,
                    max_size: field.max_size, supported_types: field.accept}
                )||{};
                if (file_errors.type)
                    return `Invalid type ${file.type}`;
                if (file_errors.max_size)
                {
                    return 'File has more than '
                        +conv.scaled_bytes(field.max_size, {base: 1024})+'B';
                }
            };
            if (field.multiple)
            {
                let errors = value&&value.map(validate_file);
                return errors&&errors.some(e=>e) ? errors : null;
            }
            return validate_file(value);
        }
        if (field.validate)
            return field.validate(value);
    }, [fields]);
    const clean_undefined = obj=>_.chain(obj).omitBy(_.isUndefined)
        .omitBy(_.isNull).value();
    const init_form_state = (_fields, entry, _opt={})=>{
        let values = {}, errors = {};
        for (let [field_id, field_opt] of Object.entries(_fields))
        {
            let value;
            if (_opt.use_initial_as_fallback)
                value = entry && entry[field_id] || field_opt.initial_value;
            else
                value = entry ? entry[field_id] : field_opt.initial_value;
            value = value ||
                (qw`text textarea`.includes(field_opt.type) ? '' : undefined);
            values = {...values, [field_id]: value};
            errors = clean_undefined({...errors,
                [field_id]: validate(field_id, values[field_id])});
        }
        return {values, errors};
    };
    const [state, dispatch] = E.use_reducer((prev, payload)=>{
        if (payload.action=='reset')
            return init_form_state(fields);
        if (payload.action=='set_entry')
            return init_form_state(fields, payload.entry);
        if (payload.action=='set_entry_use_initial_as_fallback')
        {
            return init_form_state(fields, payload.entry,
                {use_initial_as_fallback: 1});
        }
        const {values, errors} = prev;
        const id = payload.id;
        let value = payload.value;
        if (_.isFunction(value))
            value = value(values[id], values);
        return {
            values: _.set({...values}, id, value),
            errors: clean_undefined({...errors, [id]: validate(id, value)}),
        };
    }, fields, init_form_state);
    E.use_effect(()=>{
        if (opt.keep_values_on_fields_change)
        {
            return void dispatch({
                action: 'set_entry_use_initial_as_fallback',
                entry: Object.fromEntries(Object.entries(state.values).filter(
                    ([k])=>fields[k])),
            });
        }
        dispatch({action: 'reset'});
    }, [fields]);
    const on_form_change = E.use_memo(()=>(value, id)=>dispatch(
        {action: 'field_changed', value, id}), []);
    const set_entry = entry=>dispatch({action: 'set_entry', entry});
    return [state.values, on_form_change, state.errors, set_entry];
};

E.use_previous = value=>{
    const ref = E.use_ref(value);
    E.use_effect(()=>{
        ref.current = value;
    }, [value]);
    return ref.current;
};

E.use_etask_busy = ()=>{
    const etask_hook = E.use_etask();
    const [busy, set_busy] = E.use_state(false);
    return [fn=>etask_hook(function*(){
        set_busy(true);
        this.finally(()=>set_busy(false));
        return yield etask(fn);
    }), busy];
};

E.use_etask_loading_state = task_name=>{
    const etask_hook = E.use_etask();
    const [is_loading, set_loading] = E.use_state(false);
    return [fn=>etask_hook(function*(){
        this.on('uncaught', console.error);
        this.finally(()=>{
            set_loading(false);
            lt.end();
        });
        const lt = zloading.start(task_name);
        set_loading(true);
        return yield etask(fn);
    }), is_loading];
};

E.use_etask_latest = ()=>{
    let etask_hook = E.use_etask();
    let et_ref = E.use_ref();
    return fn=>etask_hook(function*(){
        if (et_ref.current)
            et_ref.current.return();
        et_ref.current = this;
        this.finally(()=>et_ref.current = null);
        return yield etask(fn);
    });
};

E.use_data_loader = (id, loader, opt={}, deps=[])=>{
    let [request_et_hook, is_request_loading] = E.use_etask_busy();
    let refresh_et_hook = E.use_etask();
    let [data, set_data] = E.use_state();
    let [load_counter, set_load_counter] = E.use_state(0);
    let [error, set_error] = E.use_state();
    let [attempted_first_load, set_attempted_first_load] = E.use_state(false);
    const loader_ref = E.use_ref();
    E.use_effect(()=>{ loader_ref.current = loader; }, [loader]);
    let on_error = E.use_cb(e=>{
        set_error(e);
        set_attempted_first_load(true);
        console.error('data_loader_error', e);
        if (opt.on_error)
            opt.on_error(e);
    }, [opt.on_error]);
    let send_request = E.use_cb(()=>{
        if (!loader_ref.current)
            return;
        set_error();
        set_load_counter(c=>c+1);
        return request_et_hook(function*(){
            this.on('uncaught', on_error);
            const l = !opt.poll &&
                zloading.start({name: id, hint: opt.loading_hint});
            this.finally(()=>{
                if (l)
                    l.end();
            });
            return yield loader_ref.current();
        });
    }, deps);
    let refresh = E.use_cb(()=>refresh_et_hook(function*(){
        let d = yield send_request();
        if (d)
            set_attempted_first_load(true);
        set_data(d);
        return d;
    }), [send_request]);
    E.use_effect(()=>{
        if (!opt.poll || error)
            return;
        let poll_interval = opt.poll===true ? date.ms.MIN : opt.poll;
        let interval_id = setInterval(refresh, poll_interval);
        return ()=>clearInterval(interval_id);
    }, [opt.poll, error, refresh]);
    E.use_effect(()=>refresh(), [refresh]);
    return E.use_object_memo({
        is_loading: !attempted_first_load || is_request_loading,
        is_first_loading: is_request_loading && load_counter==1,
        data,
        set_data,
        error,
        refresh,
    });
};

// see basic usage example in dca_schemas.js. basic idea is to give generic
// pagination-compatible way to load the data (i.e. when meta information about
// the data is needed to fetch the data itelf). cached data can be later
// accessed with a simple setdb.get call
E.use_ajax_data = (path, id_getter, use_cache)=>{
    let etask_hook = E.use_etask();
    let {view, total, db={}, cache={}, cache_id=0} = E.use_setdb(path)||{};
    let invalidate_cache = E.use_cb(()=>setdb.set(path,
        {db, cache: {}, cache_id: cache_id+1}), [cache_id]);
    let [error, set_error] = E.use_state();
    let [is_loading, set_is_loading] = E.use_state(false);
    E.use_effect(()=>()=>setdb.set(`${path}.view`, null, {depth: 1}), []);
    let use_fetch = (fetch, deps)=>E.use_effect(()=>{
        set_error(null);
        let cache_key = use_cache && JSON.stringify(deps);
        if (use_cache && cache[cache_key])
        {
            let [_view, _total] = cache[cache_key];
            return setdb.set(path,
                {view: _view, total: _total, db, cache, cache_id});
        }
        let et = etask_hook(function*(){
            this.on('uncaught', set_error);
            set_is_loading(true);
            this.finally(()=>set_is_loading(false));
            let [data, _total] = (yield fetch())||[];
            if (!data)
                return;
            let _view = _.map(data, id_getter);
            let _db = {...db, ..._.zipObject(_view, data)};
            let _cache = cache;
            if (use_cache)
                _cache = {..._cache, [cache_key]: [_view, _total]};
            setdb.set(path, {view: _view, total: _total, db: _db,
                cache: _cache, cache_id});
        });
        return ()=>et.return();
    }, [...deps, cache_id]);
    let data = E.use_memo(()=>(view||[]).map(id=>db[id]), [view, db]);
    return [data, use_fetch, {invalidate_cache,
        total, error, is_loading,
        is_empty: view && !view.length && !error && !is_loading}];
};
E.invalidate_ajax_cache = path=>{
    const {db, cache_id} = setdb.get(path)||{};
    if (!db)
        return;
    setdb.set(path, {db, cache: {}, cache_id: cache_id+1});
};
E.update_ajax_db = (path, id, entry)=>{
    const {db, ...data} = setdb.get(path)||{};
    setdb.set(path, {db: {...db, [id]: entry}, ...data});
};


E.use_list_pagination = (list, opt={})=>{
    const [page, set_page] = E.use_state(1);
    const [page_size, set_page_size] = E.use_state(opt.page_size || 30);
    const [total_pages, set_total_pages] = E.use_state(0);
    const [shown_count, set_shown_count] = E.use_state(0);
    const [data, set_data] = E.use_state([]);
    const [prev_available, set_prev_available] = E.use_state(false);
    const [next_available, set_next_available] = E.use_state(false);

    // Calculate total pages
    E.use_effect(()=>{
        const new_total_pages = Math.ceil((list||[]).length / page_size);
        set_total_pages(new_total_pages);
        set_page(1);
    }, [list, page_size]);

    // Update visible data based on the current page
    E.use_effect(()=>{
        const start = (page - 1) * page_size;
        const end = start + page_size;
        const new_data = (list||[]).slice(start, end);
        set_data(new_data);
        set_shown_count(new_data.length);
        set_prev_available(page > 1);
        set_next_available(page < total_pages);
    }, [list, page, page_size, total_pages]);

    // Function to set page directly
    const set_page_directly = E.use_cb(page_number=>{
        const new_page = Math.max(1, Math.min(page_number, total_pages));
        set_page(new_page);
    }, [total_pages]);

    // Functions to navigate to the next and previous pages
    const next = E.use_cb(()=>{
        set_page(current_page=>current_page < total_pages
            ? current_page + 1
            : current_page);
    }, [total_pages]);

    const prev = E.use_cb(()=>{
        set_page(current_page=>current_page > 1
            ? current_page - 1
            : current_page);
    }, []);

    return {page, page_size, total_pages, prev_available, next_available,
        next, prev, set_page_size, shown_count, set_page: set_page_directly,
        list_data: data};
};


E.use_simple_pagination = (total, per_page)=>{
    const prev_per_page = E.use_previous(per_page);
    const [page, on_page_change] = E.use_state(1);
    const total_pages = Math.ceil(total/per_page);
    const set_page = E.use_cb(p=>on_page_change(Math.max(1,
        Math.min(p, total_pages))||1), [total_pages]);
    E.use_effect(()=>
        on_page_change(p=>Math.max(1, Math.min(p, total_pages))||1), [total]);
    E.use_effect(()=>on_page_change(p=>{
        const start = ((p||1)-1)*prev_per_page;
        return Math.round(start/per_page+1);
    }), [per_page]);
    const on_prev = E.use_cb(()=>on_page_change(p=>Math.max(1, p-1)), []);
    const on_next = E.use_cb(()=>on_page_change(p=>Math.min(p+1,
        Math.ceil(total/per_page))), [total, per_page]);
    const reset = E.use_cb(()=>on_page_change(1), []);
    const start = (page-1)*per_page;
    const end = Math.min(start+per_page, total);
    return {page, total_pages, set_page, start, end, reset, on_prev, on_next};
};

E.use_resize_cb = (el, cb)=>{
    let observer = E.use_memo(()=>cb&&window.ResizeObserver&&
    new ResizeObserver(entries=>{
        let {width, height} = entries[0].contentRect;
        cb({width, height});
    }), [cb]);
    E.use_effect(()=>{
        if (!el||!observer)
            return;
        observer.observe(el);
        return ()=>observer.disconnect();
    }, [el, observer]);
};

E.use_resize_observer = ref=>{
    const [size, set_size] = E.use_state({width: 0, height: 0,
        scrollbar_height: 0, scrollbar_width: 0});
    const observer = E.use_ref(window.ResizeObserver&&
    new ResizeObserver(entries=>{
        const {width, height} = entries[0].contentRect;
        const scrollbar_height = entries[0].target.offsetHeight -
            entries[0].target.clientHeight;
        const scrollbar_width = entries[0].target.offsetWidth -
            entries[0].target.clientWidth;
        set_size({width, height, scrollbar_height, scrollbar_width});
    }));
    E.use_effect(()=>{
        if (!(ref.current&&observer.current))
            return;
        observer.current.observe(ref.current);
        return ()=>observer.current.disconnect();
    }, [ref.current, observer]);
    return size;
};

E.use_mutation_observer = (ref, cb, opt)=>{
    let [observer, set_observer] = E.use_state(null);
    E.use_effect(()=>set_observer(new MutationObserver(cb)), [cb]);
    E.use_effect(()=>{
        if (!observer||!ref.current)
            return;
        observer.observe(ref.current,
            opt || {attributes: true, childList: true, subtree: true});
        return ()=>observer && observer.disconnect();
    }, [observer, ref.current, opt]);
};

E.use_children_count = ref=>{
    let [count, set_count] = E.use_state(
        _.get(ref.current, 'children.length', 0));
    E.use_mutation_observer(ref, E.use_cb(mutation_ev=>{
        set_count(_.get(mutation_ev, '0.target.children.length', 0));
    }, []), {childList: true});
    return count;
};

E.use_outside_click = (ref, on_click)=>{
    const handle_click = E.use_cb(e=>{
        if (ref.current && !ref.current.contains(e.target))
            on_click(e);
    }, [ref, on_click]);
    E.use_effect(()=>{
        const use_capture = true;
        document.addEventListener('mousedown', handle_click, use_capture);
        return ()=>document.removeEventListener('mousedown', handle_click,
            use_capture);
    }, [handle_click]);
};

E.use_outside_rect_click = (elements, on_click)=>E.use_effect(()=>{
    if (!elements?.length||!on_click)
        return;
    let handle_click = ev=>{
        let {clientX: x, clientY: y} = ev;
        if (!elements.filter(Boolean).some(el=>{
            let {top, left, bottom, right} = el.getBoundingClientRect();
            return x>=left && y>=top && x<=right && y<=bottom;
        }))
        {
            on_click();
        }
    };
    window.addEventListener('mousedown', handle_click, true);
    return ()=>window.removeEventListener('mousedown', handle_click, true);
}, [elements, on_click]);

E.use_once = (cb, condition)=>{
    condition = condition===undefined ? true : condition;
    let is_called_ref = E.use_ref(false);
    E.use_effect(()=>{
        if (!condition || is_called_ref.current)
            return;
        is_called_ref.current = true;
        cb();
    }, [condition]);
};

// use use_data_loader instead
E.use_data_fetcher = (fetcher, deps=[], opt={})=>{
    const [is_loading, set_is_loading] = E.use_state(false);
    const [data, set_data] = E.use_state();
    const [error, set_error] = E.use_state();
    const et_ref = E.use_ref();
    const send_request = E.use_cb(()=>etask(function*(){
        this.on('uncaught', e=>set_error(e));
        this.finally(()=>set_is_loading(false));
        set_error();
        set_is_loading(true);
        let et = et_ref.current = (fetcher||_.noop)();
        let res = yield et;
        set_data(res);
        return res;
    }), deps);
    E.use_effect(()=>{ send_request(); }, [send_request]);
    E.use_effect(()=>()=>et_ref.current?.return(), []);
    return {data, error, is_loading, refresh: send_request};
};

E.use_url_change_refresh = history=>{
    const set_refresh = E.use_state()[1];
    E.use_effect(()=>history.listen(()=>set_refresh(v=>!v)), [history]);
};

const normalize_by_schema = (obj, schema)=>{
    const resolve_type = schema_field=>{
        const is_array = Array.isArray(schema_field);
        const field_type = is_array ? schema_field[0]: schema_field;
        if (field_type==Number)
            return {is_array, type: 'number'};
        if (field_type==Boolean)
            return {is_array, type: 'boolean'};
        if (field_type==String)
            return {is_array, type: 'string'};
        if (field_type==Date)
            return {is_array, type: 'date'};
    };
    const validators = {
        number: {validate: v=>Number.isFinite(+v), normalize: v=>+v},
        boolean: {
            validate: v=>Number.isFinite(+v) ||
                ['true', 'false'].includes((v||'').toLowerCase()),
            normalize: v=>Number.isFinite(+v)
                ? !!+v : v.toLowerCase()=='true' ? true : false,
        },
        string: {validate: v=>typeof v=='string', normalize: v=>v},
        date: {
            validate: v=>!v.length||date(v).toString()!=='Invalid Date',
            normalize: v=>v.length ? date(v) : null,
        },
    };
    const result = {};
    for (const key in schema)
    {
        if (!obj.hasOwnProperty(key))
            continue;
        const {type, is_array} = resolve_type(schema[key]);
        const {validate, normalize} = validators[type];
        const values = is_array ? obj[key].split(',') : [obj[key]];
        if (values.some(v=>!validate(v)))
            continue;
        const normalized = values.map(normalize);
        result[key] = is_array ? normalized : normalized[0];
    }
    return result;
};
// Usage:
// const [filters, set_filters] = use_qs_params(history, {
//     [param_name_1]: String,
//     [param_name_2]: Boolean,
//     [param_name_3]: [String] // supports arrays as well
// }, {replace: 1});
// - Filters will contain only fields in the schema and be normalized.
// - Can also provide param name (or array) as schema. Type defaults to String.
// - Can provide {replace: 1} to use history.replace instead of push
E.use_qs_params = (history, schema_or_array, opt={})=>{
    E.use_url_change_refresh(history);
    const ref = E.use_ref();
    const schema = E.use_memo(()=>{
        if (_.isObject(schema_or_array))
            return schema_or_array;
        return Object.values(_.castArray(schema_or_array))
            .reduce((acc, k)=>({...acc, [k]: String}), {});
    }, [JSON.stringify(schema_or_array)]);
    ref.current = {schema, history, opt};
    const set_params = E.use_cb(values_or_cb=>{
        const {schema: _schema, history: _history, opt: _opt} = ref.current;
        const qs_o = zurl.qs_parse_url(_history.location.search);
        const fields = Object.keys(_schema);
        const values = _.isFunction(values_or_cb)
            ? values_or_cb(_.pick(qs_o, fields))
            : values_or_cb;
        const new_qs_o = {
            ..._.omit(qs_o, fields),
            ..._.pick(values, fields),
        };
        if (_.isEqual(qs_o, new_qs_o))
            return;
        _history[_opt?.replace ? 'replace' : 'push']({
            search: zurl.qs_str(new_qs_o),
        });
    }, []);
    const qs_o = E.use_memo(()=>{
        return zurl.qs_parse_url(history.location.search);
    }, [history.location.search]);
    const [normalized, set_normalized] = E.use_state(()=>
        normalize_by_schema(qs_o, schema));
    // only update if qs fields that were provided changed
    E.use_effect(()=>{
        const {schema: _schema} = ref.current;
        const _normalized = normalize_by_schema(qs_o, _schema);
        if (!_.isEqual(normalized, _normalized))
            set_normalized(_normalized);
    }, [qs_o]);
    return [normalized, set_params];
};

E.use_qs_param = (history, name, type=String, opt)=>{
    const [value, _set_value] = E.use_qs_params(history, {[name]: type}, opt);
    const set_value = E.use_cb(v_or_cb=>_set_value(prev=>{
        if (v_or_cb===undefined)
            return;
        return {[name]: _.isFunction(v_or_cb) ? v_or_cb(prev[name]) : v_or_cb};
    }), [_set_value]);
    return [value[name], set_value];
};

E.use_url_param = (history, match, name, opt={})=>{
    const value = match.params[name];
    const set_value = E.use_cb((v, _opt={})=>{
        const pathname =
            generatePath(match.path, {...match.params, [name]: v});
        if (pathname==history.location.pathname)
            return;
        history[opt.replace || _opt.replace ? 'replace' : 'push'](
            {pathname: pathname, search: history.location.search});
    }, [history, match]);
    const normalized = E.use_memo(()=>{
        if (!opt.normalize)
            return value;
        return opt.normalize(value);
    }, [value, opt.normalize]);
    E.use_once(()=>{
        let def;
        if (opt.qs_parse)
        {
            const qs_o = zurl.qs_parse_url(history.location.search);
            def = qs_o[_.isString(opt.qs_parse) ? opt.qs_parse: name];
            if (def)
                history.replace({search: zurl.qs_str(_.omit(qs_o, name))});
        }
        def = def||opt.default;
        if (value || !def || !(name in match.params))
            return;
        set_value(def, {replace: 1});
    }, !!(match && history));
    return [normalized, set_value, value];
};

E.use_debounced_qs_search = (history, debounce=400)=>{
    E.use_url_change_refresh(history);
    const qs = history.location.search;
    const qs_o = E.use_memo(()=>zurl.qs_parse_url(qs), [qs]);
    const [search, search_debounced, _set_search] =
        E.use_debounced_state(qs_o.search, debounce);
    const synced_ref = E.use_ref(true);
    const set_search = E.use_cb(v=>{
        synced_ref.current = false;
        _set_search(v);
    }, []);
    E.use_effect(()=>{
        const no_change = search_debounced==qs_o.search ||
            _.isNil(qs_o.search) && search_debounced=='';
        if (no_change)
            return;
        if (!synced_ref.current)
        {
            const new_qs_o = _.cloneDeep(qs_o);
            if (_.isEmpty(search_debounced))
                delete new_qs_o.search;
            else
                new_qs_o.search = search_debounced;
            history.replace({search: zurl.qs_str(new_qs_o)});
        }
        else // if qs changed and synced==true, change came from outside
            _set_search(qs_o.search);
        synced_ref.current = true;
    }, [qs_o, search_debounced]);
    return {search, search_debounced, set_search};
};

E.use_portal = ()=>{
    let portal_ref = E.use_ref();
    E.use_effect(()=>{
        let p = portal_ref.current = document.createElement('div');
        p.setAttribute('data-info', 'portal');
        document.body.appendChild(p);
        return ()=>document.body.removeChild(p);
    }, []);
    let Portal = E.use_memo(()=>({children})=>{
        return portal_ref ?
            ReactDOM.createPortal(children, portal_ref.current) : children;
    }, [portal_ref]);
    return Portal;
};

E.use_reactive_state = value=>{
    let [state, set_state] = E.use_state(value);
    E.use_effect(()=>{ set_state(value); }, [value]);
    return [state, set_state];
};

E.use_zmixpanel_context = (name, data={})=>{
    let _data = E.use_object_memo(data);
    E.use_effect(()=>{
        if (window.zmixpanel?.has_context(name))
            window.zmixpanel.remove_context(name);
        window.zmixpanel?.add_context(name, _data);
        return ()=>window.zmixpanel?.remove_context(name);
    }, [_data]);
    return _data;
};

// Track events with similar properties without adding context prefixes.
// Use hook:
// let track = use_zmixpanel_track({
//     dataset_id: dataset?.id,
// });
// Track events:
// track('Clicked button'); // Without extra payload
// track('Clicked button', {with_extra_payload: true});
E.use_zmixpanel_track = (data={})=>{
    let _data = E.use_object_memo(data);
    return E.use_cb((event, payload={})=>window.zmixpanel.track(event,
        assign({}, _data, payload)), [_data]);
};

E.use_root = ()=>!!E.use_setdb('login.user.is_root');

// Opens url in new tab if middle mouse button clicked or ctrl is down,
// otherwise pushes to history. Click is disabled if no url provided.
// Usage: destructure returned object into JSX element props:
// let open_in_new_tab = use_open_in_new_tab(history, url);
// return <div {...open_in_new_tab}></div>
E.use_open_in_new_tab = (history, url, on_click, new_tab)=>{
    let open_in_new_tab = E.use_cb(()=>{
        let _url = on_click?.();
        window.open(_url||url, '_blank');
    }, [on_click, url]);
    let _on_click = E.use_cb(ev=>{
        if (!url)
            return;
        if (ev.ctrlKey || new_tab)
            return void open_in_new_tab();
        let _url = on_click?.();
        history.push(_url||url);
    }, [on_click, url, open_in_new_tab, history, new_tab]);
    let on_mouse_down = E.use_cb(ev=>url && ev.button==1 && open_in_new_tab(),
        [url, open_in_new_tab]);
    return {onClick: _on_click, onMouseDown: on_mouse_down};
};

E.use_scroll = ref=>{
    let [event_pos, set_event_pos] = E.use_state(0);
    let on_scroll = E.use_cb(_.throttle(
        ()=>set_event_pos(ref.current?.scrollTop||0), 500), []);
    E.use_event_listener(ref.current, 'scroll', on_scroll);
    let set_pos = E.use_cb(top=>{
        ref.current?.scrollTo({top, behavior: 'smooth'});
        set_event_pos(top);
    }, []);
    return [event_pos, set_pos];
};

E.use_viewport_entry = (ref, opt)=>{
    let _opt = E.use_object_memo(opt);
    let [entered, set_entered] = E.use_state();
    let [observer, set_observer] = E.use_state();
    E.use_effect(()=>set_observer(new IntersectionObserver(
        ([entry])=>set_entered(entry.isIntersecting), _opt)),
        [_opt]);
    E.use_effect(()=>{
        if (!observer||!ref?.current)
            return;
        if (entered)
        {
            observer.disconnect();
            return;
        }
        observer.observe(ref.current);
        return ()=>observer.disconnect();
    }, [observer, entered, ref?.current]);
    return entered;
};

E.use_live_ref = (target, deps=[])=>{
    let ref = E.use_ref(target);
    E.use_effect(()=>{ ref.current = target; }, [target, ...deps]);
    return ref;
};

// The hook doesn't block navigation
// It prompts the user with a confirmation modal
E.use_nav_guard = (cond, msg)=>{
    let _msg = E.use_memo(()=>msg||'Are you sure you want to leave?', [msg]);
    E.use_effect(()=>{
        if (!cond)
            return;
        let handler = e=>{
            e.preventDefault();
            e.returnValue = _msg;
            return _msg;
        };
        window.addEventListener('beforeunload', handler);
        return ()=>window.removeEventListener('beforeunload', handler);
    }, [cond, _msg]);
    return <Prompt when={cond} message={_msg}/>;
};

// set onMouseDown={on_mouse_down} on the component controlling the drag
E.use_drag = (opt={})=>{
    let {on_drag_start, on_drag_end} = opt;
    let on_drag_start_ref = E.use_live_ref(on_drag_start);
    let on_drag_end_ref = E.use_live_ref(on_drag_end);
    let dragging_ref = E.use_ref();
    let start_pos = E.use_ref({x: 0, y: 0});
    let [pos, set_pos] = E.use_state({x: 0, y: 0});
    let _set_pos = E.use_cb(v=>{
        start_pos.current = {x: 0, y: 0};
        set_pos(v);
    }, []);
    let on_mouse_move = E.use_cb(ev=>{
        if (!dragging_ref.current)
            return;
        ev.preventDefault();
        set_pos({
            x: ev.clientX-start_pos.current.x,
            y: ev.clientY-start_pos.current.y,
        });
    }, []);
    let on_mouse_up = E.use_cb(()=>{
        if (dragging_ref.current)
            on_drag_end_ref.current?.();
        dragging_ref.current = false;
    }, []);
    let on_mouse_down = E.use_cb(ev=>{
        ev.preventDefault();
        dragging_ref.current = true;
        start_pos.current = {x: ev.clientX-pos.x, y: ev.clientY-pos.y};
        on_drag_start_ref.current?.();
    }, [pos]);
    E.use_event_listener(window, 'mousemove', on_mouse_move, [on_mouse_move]);
    E.use_event_listener(window, 'mouseup', on_mouse_up, [on_mouse_up]);
    return {pos, set_pos: _set_pos, on_mouse_down};
};

E.use_hotkey = (key, cb, enabled=true)=>E.use_effect(()=>{
    if (!key||!cb||!enabled)
        return;
    let _cb = ev=>ev.key==key&&cb(ev);
    window.addEventListener('keydown', _cb);
    return ()=>window.removeEventListener('keydown', _cb);
}, [key, cb, enabled]);

E.use_text = (namespace, key_prefix)=>{
    let {t, i18n} = useTranslation(namespace, {keyPrefix: key_prefix});
    return E.use_cb((key, opt={})=>{
        let _key = `${namespace}:${key_prefix}.${key}`;
        if (i18n.exists(_key))
        {
            let {components, values, return_objects} = opt;
            if (!components)
                return t(key, {...values, returnObjects: return_objects});
            return <Trans i18nKey={_key} components={components}
              values={values}/>;
        }
    }, [t, i18n, namespace, key_prefix]);
};

E.use_breakpoints = (ref, brks)=>{
    let _brks = E.use_object_memo(brks||{lg: 1000, md: 700, sm: 0});
    let sorted_brks = E.use_memo(()=>_.entries(_brks).sort((a, b)=>a[1]-b[1]),
        [_brks]);
    let default_brk = E.use_memo(()=>sorted_brks[sorted_brks.length-1],
        [sorted_brks]);
    let find_brk = E.use_cb(width=>
        (sorted_brks.findLast(_b=>_b[1]<=width)||default_brk)[0],
        [sorted_brks, default_brk]);
    let [brk, set_brk] = E.use_state(()=>find_brk(ref.current?.offsetWidth));
    let _set_brk = E.use_cb(({width})=>set_brk(find_brk(width)), [find_brk]);
    E.use_resize_cb(ref.current, _set_brk);
    return brk;
};

const SET_MUTATING_METHODS = qw`add delete clear`;
E.use_reactive_set = initial=>{
    let [value, set_value] = E.use_state(new Set(initial));
    return E.use_memo(()=>{
        let res = {};
        Object.getOwnPropertyNames(Set.prototype).forEach(m=>{
            if (m=='constructor')
                return;
            if (SET_MUTATING_METHODS.includes(m))
            {
                res[m] = (...args)=>set_value(prev=>{
                    let new_set = new Set(prev);
                    new_set[m](...args);
                    return new_set;
                });
                return;
            }
            res[m] = _.isFunction(value[m]) ? value[m].bind(value) : value[m];
        });
        return res;
    }, [value]);
};

E.use_typing_animation = (text, opt={})=>{
    let [value, set_value] = E.use_state('');
    let opt_ref = E.use_live_ref(opt);
    let text_ref = E.use_live_ref(text);
    E.use_effect(()=>{
        let timeout, idx = 0, chars = 0, forward = true;
        let render = ()=>{
            let {enabled, speed=50, type_chars=2,
                full_text_delay=4000} = opt_ref.current;
            let _text = text_ref.current;
            if (!_text?.length||enabled===false)
            {
                idx = chars = 0;
                forward = true;
                set_value('');
                timeout = setTimeout(render, 2000);
                return;
            }
            if (_.isString(_text))
                _text = [_text];
            let p = _text[idx], delay = speed;
            if (forward)
            {
                if (chars<p.length)
                    chars = Math.min(chars+type_chars, p.length);
                if (chars==p.length)
                {
                    delay = full_text_delay;
                    forward = false;
                }
            }
            else
            {
                if (chars>=0)
                    chars = Math.max(chars-type_chars, 0);
                if (chars==0)
                {
                    idx = (idx+1)%_text.length;
                    forward = true;
                }
            }
            set_value(p.substring(0, chars));
            timeout = setTimeout(render, delay);
        };
        timeout = setTimeout(render, opt_ref.current.speed||50);
        return ()=>clearTimeout(timeout);
    }, []);
    return value;
};

return E; }); // eslint-disable-line
